import React, { useState, useEffect, useRef } from 'react'
import classnames from 'classnames'
import * as timeago from 'timeago.js'
import map from 'lodash/map'
import getProjectStatusForProjectsList from '@Utils/GetProjectStatusForProjectsList'
import getShouldAllowToAddLanguages from '@Utils/GetShouldAllowToAddLanguages'
import getShouldAllowToGenerateClips from '@Utils/GetShouldAllowToGenerateClips'
import getCleanDisplayTitle from '@Utils/GetCleanDisplayTitle'
import useTooltipDelay from '@Utils/UseTooltipDelay'
import TitleTooltip from '@SiteComponents/TitleTooltip'
import ProjectActionDropdown from './ProjectActionDropdown'
import More from '@Images/projects_table_grid_more.svg'
import Dot from '@Images/projects_table_grid_dot.svg'
import Clock from '@Images/projects_table_grid_clock.svg'

import {
  IProject,
} from '@SiteModules/Project/types'

import {
  IProjectVersion,
} from '@SiteModules/ProjectVersion/types'

import {
  IOpenRenameProjectPopupPayload,
  IOpenDeleteProjectPopupPayload,
  IOpenSetupProjectPopupPayload,
  IOpenSetupProjectPopupTtsPayload,
} from '@SiteModules/Popup/types'

import './ProjectCard.scss'

const MILLISECONDS_IN_SECONDS = 1000
const LIMIT_FOR_LABELS_IN_FIRST_LINE = 300 // px card width
const TITLE_EMOJI = '🎬'
const TITLE_EMOJI_TTS = '🗣'
const TITLE_EMOJI_TRANSLATIONS = '🌐'

interface ProjectCardProps {
  readonly project: IProject
  readonly originalProjectVersion: IProjectVersion
  readonly selected: boolean
  readonly labelCaptionsOfProject: string[]
  readonly cardWidth: number
  readonly cardHeight: number
  readonly thumbnailHeight: number
  readonly thumbnailPadding: number
  readonly currentAccountHasCurrentSubscription: boolean
  changeSelectedProjects(e: React.ChangeEvent<HTMLInputElement>, projectId: string): void
  handleForwarding(project: IProject): void
  handleOpenRenameProjectPopup(payload: IOpenRenameProjectPopupPayload): void
  handleOpenDeleteProjectPopup(payload: IOpenDeleteProjectPopupPayload): void
  handleOpenSetupProjectPopup(payload: IOpenSetupProjectPopupPayload): void
  handleOpenSetupProjectPopupTts(payload: IOpenSetupProjectPopupTtsPayload): void
  handleOpenRenewSubscriptionPopup(): void
  handleOpenExportationPopup(projectId: string): void
}

const ProjectCard: React.FC<ProjectCardProps> = props => {
  const {
    selected,
    cardWidth,
    cardHeight,
    thumbnailHeight,
    thumbnailPadding,
    originalProjectVersion,
    project,
    labelCaptionsOfProject,
    currentAccountHasCurrentSubscription,
  } = props

  const {
    id,
    projectVersions,
  } = project

  const {
    title,
  } = project.attributes

  const {
    subtitles,
  } = originalProjectVersion

  const {
    status,
    duration,
    thumbnailUrl,
    belongsToTtsProject,
    mostRecentUpdatedAtTimestamp,
    isPreparingRepurposedClips,
  } = originalProjectVersion.attributes

  const {
    showTooltip,
    setShowTooltip,
    triggerShowTooltip,
    cancelTooltip,
  } = useTooltipDelay()

  const imgRef = useRef<HTMLImageElement | null>(null)
  const [showCheckbox, setShowCheckbox] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [fitClass, setFitClass] = useState('cover')

  const isDownloading = status === 'downloading'
  const shouldShowLinkToEditor = subtitles.length === 0 && !isDownloading && !isPreparingRepurposedClips
  const shouldOpenLanguagesView = projectVersions.length === 1 && (subtitles.length > 0 || isPreparingRepurposedClips)
  const shouldOpenProjectVersionsView = projectVersions.length > 1 && subtitles.length > 0
  const shouldShowLinkForName = shouldShowLinkToEditor || shouldOpenLanguagesView || shouldOpenProjectVersionsView
  const shouldAllowToAddLanguages = getShouldAllowToAddLanguages(originalProjectVersion.attributes)
  const shouldAllowToGenerateClips = getShouldAllowToGenerateClips(originalProjectVersion.attributes, originalProjectVersion.subtitles)
  const titleElementId = `project_card_title_${id}`
  const moreElementId = `project_card_more_${id}`
  const classNameForClickOutsideListenerMoreDropdown = `ProjectsTable__action-dropdown-element-${id}`
  const numberOfLabels = labelCaptionsOfProject.length
  const showLabelsInSecondLine = cardWidth < LIMIT_FOR_LABELS_IN_FIRST_LINE
  const numberOfClips = projectVersions.length - 1
  const hasClips = numberOfClips > 0
  const hasTranslationsOfOriginalAndNoClips = originalProjectVersion.subtitles.length > 1 && !hasClips
  const cleanTitle = getCleanDisplayTitle(title)

  useEffect(() => {
    const img = imgRef.current
    if (img && img.complete) {
      applyFitClass(img)
    } else if (img) {
      img.onload = () => applyFitClass(img)
    }
  }, [])

  const applyFitClass = (img: HTMLImageElement) => {
    const { naturalWidth, naturalHeight } = img
    if (naturalWidth >= naturalHeight) {
      setFitClass('cover')
    } else {
      setFitClass('contain')
    }
  }

  const onMouseEnterCard = () => {
    setShowCheckbox(true)
  }

  const onMouseLeaveCard = () => {
    setShowCheckbox(false)
  }

  const onMouseEnterTitle = () => {
    triggerShowTooltip()
  }

  const onMouseLeaveTitle = () => {
    cancelTooltip()
  }

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown)
  }

  const handleProjectSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.changeSelectedProjects(e, id)
  }

  const handleClickOnTitleOrThumbnail = () => {
    if (shouldShowLinkForName) {
      props.handleForwarding(project)
    }
  }

  const handleOpenRenameProjectPopup = () => {
    props.handleOpenRenameProjectPopup({ projectId: id })
    setShowDropdown(false)
  }

  const handleOpenDeleteProjectPopup = () => {
    props.handleOpenDeleteProjectPopup({ projectIds: [id] })
    setShowDropdown(false)
  }

  const handleOpenSetupProjectOrRenewSubscriptionPopup = (projectType?: string) => {
    if (currentAccountHasCurrentSubscription) {
      belongsToTtsProject ? props.handleOpenSetupProjectPopupTts({ projectVersionIds: [originalProjectVersion.id] }) : props.handleOpenSetupProjectPopup({ projectVersionIds: [originalProjectVersion.id], projectType })
    } else {
      props.handleOpenRenewSubscriptionPopup()
    }

    setShowDropdown(false)
  }

  const handleOpenExportationPopup = () => {
    props.handleOpenExportationPopup(project.id)
    setShowDropdown(false)
  }

  const renderThumbnail = () => {
    const shouldShowColoredThumbnail = thumbnailUrl.includes('thumbnail-icon.png') || !thumbnailUrl
    const indexOfFirstIntegerInId = id.search(/[0-9]/)
    const colorPicker = indexOfFirstIntegerInId === -1 ? 0 : id[indexOfFirstIntegerInId]
    const thumbnailStyle = {
      width: `${cardWidth}px`,
      height: `${thumbnailHeight}px`,
    }

    const placeHolderStyle = {
      width: `${cardWidth - 2 * thumbnailPadding}px`,
      height: `${thumbnailHeight - 2 * thumbnailPadding}px`,
    }

    return (
      <div
        className={classnames(
          'ProjectCard__thumbnail',
          { 'ProjectCard__thumbnail-link': shouldShowLinkForName }
        )}
        onClick={handleClickOnTitleOrThumbnail}
        style={thumbnailStyle}
      >
        {shouldShowColoredThumbnail ? (
          <div
            className={classnames(
              'ProjectCard__thumbnail-placeholder',
              `ProjectsTable__thumbnail-placeholder${colorPicker}`,
              { 'ProjectCard__thumbnail-link': shouldShowLinkForName }
            )}
            style={placeHolderStyle}
          />
        ) : (
          <div
            className="ProjectCard__thumbnail-image-wrapper with-blur"
            style={placeHolderStyle}
          >
            <img
              className="ProjectCard__thumbnail-image background-blur"
              src={thumbnailUrl}
              aria-hidden="true"
              loading="lazy"
              decoding="async"
            />
            <img
              className={`ProjectCard__thumbnail-image foreground ${fitClass}`}
              src={thumbnailUrl}
              alt="Project thumbnail"
              ref={imgRef}
              loading="lazy"
              decoding="async"
            />
          </div>
        )}
        {renderThumbnailInfos()}
      </div>
    )
  }

  const renderThumbnailInfos = () => {
    const statuses = map(project.projectVersions, pv => pv.attributes.status)
    const [projectStatusClass, projectStatusToRender] = getProjectStatusForProjectsList(statuses)
    const shouldShowCheckbox = selected || showCheckbox

    return (
      <React.Fragment>
        <div
          className={classnames(
            'ProjectCard__status-badge-and-labels',
            { 'ProjectCard__status-badge-and-labels-two-lines': showLabelsInSecondLine }
          )}
        >
          <div className={`ProjectCard__status-badge ProjectsTable__status-badge ${projectStatusClass}`}>
            {projectStatusToRender}
          </div>
          {numberOfLabels > 0 && (
            <div
              className={classnames(
                'ProjectCard__labels',
                { 'ProjectCard__labels-two-lines': showLabelsInSecondLine }
              )}
            >
              <div className="ProjectCard__labels-first">
                <div className="ProjectCard__labels-first-caption">
                  {labelCaptionsOfProject[0]}
                </div>
              </div>
              {numberOfLabels > 1 && (
                <div className="ProjectCard__labels-others">
                  +{numberOfLabels - 1}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="ProjectCard__duration">
          <Clock />
          {duration}
        </div>
        {shouldShowCheckbox && renderCheckbox()}
      </React.Fragment>
    )
  }

  const renderTooltip = () => {
    return (
      <TitleTooltip
        id={id}
        title={cleanTitle}
        elementId={titleElementId}
        tooltipOffsetX={-50}
        tooltipOffsetY={8}
        classNameOfScrollContainer="ProjectsTableGrid"
        setShowTooltip={setShowTooltip}
      />
    )
  }

  const renderBottom = () => {
    return (
      <div className="ProjectCard__bottom">
        {renderTitleAndMore()}
        {renderInfos()}
      </div>
    )
  }

  const renderTitleAndMore = () => {
    return (
      <div className="ProjectCard__title-and-more-wrapper">
        {renderTitle()}
        <div
          className={`ProjectCard__more-wrapper ${classNameForClickOutsideListenerMoreDropdown}`}
          id={moreElementId}
        >
          <More
            onClick={toggleDropdown}
          />
          {showDropdown && renderActionDropdown()}
        </div>
      </div>
    )
  }

  const renderActionDropdown = () => {
    return (
      <ProjectActionDropdown
        showDropdown={showDropdown}
        projectId={id}
        canManageProjectSharing={project.attributes.canManageProjectSharing}
        classNameForClickOutsideListenerMoreDropdown={classNameForClickOutsideListenerMoreDropdown}
        moreElementId={moreElementId}
        setShowDropdown={setShowDropdown}
        shouldAllowToAddLanguages={shouldAllowToAddLanguages}
        shouldAllowToGenerateClips={shouldAllowToGenerateClips}
        openSetupProjectOrRenewSubscriptionPopup={handleOpenSetupProjectOrRenewSubscriptionPopup}
        openDeleteProjectPopup={handleOpenDeleteProjectPopup}
        openRenameProjectPopup={handleOpenRenameProjectPopup}
        listActive={false}
        handleOpenExportationPopup={handleOpenExportationPopup}
      />
    )
  }

  const renderTitle = () => {
    const shouldShowTooltip = showTooltip && !showDropdown

    let titleEmoji = TITLE_EMOJI
    if (belongsToTtsProject) {
      titleEmoji = TITLE_EMOJI_TTS
    } else if (hasTranslationsOfOriginalAndNoClips) {
      titleEmoji = TITLE_EMOJI_TRANSLATIONS
    }

    return (
      <div
        className="ProjectCard__title-inner-wrapper"
        onMouseEnter={onMouseEnterTitle}
        onMouseLeave={onMouseLeaveTitle}
        id={titleElementId}
      >
        <div
          className={classnames(
            'ProjectCard__title',
            { 'ProjectCard__title-link': shouldShowLinkForName }
          )}
          onClick={handleClickOnTitleOrThumbnail}
        >
          {titleEmoji} {cleanTitle}
        </div>
        {shouldShowTooltip && renderTooltip()}
      </div>
    )
  }

  const renderInfos = () => {
    const numberOfLanguages = subtitles.length

    return (
      <div className="ProjectCard__infos">
        {hasClips && (
          <React.Fragment>
            <div>
              {numberOfClips} {numberOfClips === 1 ? 'clip' : 'clips'}
            </div>
            <Dot />
          </React.Fragment>
        )}
        {!hasClips && isPreparingRepurposedClips && (
          <React.Fragment>
            <div className="ProjectCard__clips-preparing">
              Preparing clips
            </div>
            <Dot />
          </React.Fragment>
        )}
        <div>
          {numberOfLanguages} {numberOfLanguages === 1 ? 'language' : 'languages'}
        </div>
        <Dot />
        <div>
          {timeago.format(mostRecentUpdatedAtTimestamp * MILLISECONDS_IN_SECONDS)}
        </div>
      </div>
    )
  }

  const handleCheckboxClick = (e: React.MouseEvent<HTMLLabelElement>) => {
    e.stopPropagation()
  }

  const renderCheckbox = () => {
    return (
      <label
        className="ProjectsTable__checkbox ProjectCard__checkbox"
        onClick={handleCheckboxClick}
      >
        <input
          type="checkbox"
          onChange={handleProjectSelection}
          checked={selected}
        />
        <div className="ProjectCard__checkmark" />
      </label>
    )
  }

  const cardStyle = {
    width: `${cardWidth}px`,
    height: `${cardHeight}px`,
  }

  return (
    <div
      className="ProjectCard"
      onMouseEnter={onMouseEnterCard}
      onMouseLeave={onMouseLeaveCard}
      style={cardStyle}
    >
      {renderThumbnail()}
      {renderBottom()}
    </div>
  )
}

export default ProjectCard
